import { CdkVirtualScrollViewport, VirtualScrollStrategy } from '@angular/cdk/scrolling';
import { distinctUntilChanged, Observable, Subject } from 'rxjs';

export class CustomVirtualScrollStrategy implements VirtualScrollStrategy {
  scrolledIndexChange$ = new Subject<number>();
  scrolledIndexChange: Observable<number> = this.scrolledIndexChange$.pipe(distinctUntilChanged());
  private viewport: CdkVirtualScrollViewport | null;
  private heights: number[] = [];
  private topOffset = 0;
  private bottomOffset = 0;

  attach(viewport: CdkVirtualScrollViewport): void {
    this.viewport = viewport;

    if (this.heights) {
      this.viewport.setTotalContentSize(this.getTotalHeight());
      this.updateRenderedRange();
    }
  }

  detach(): void {
    this.viewport = null;
  }

  updateTopOffset(offset: number) {
    this.topOffset = offset;
  }

  updateBottomOffset(offset: number) {
    this.bottomOffset = offset;
  }

  updateHeights(heights: number[]) {
    this.heights = heights;

    if (this.viewport) {
      this.viewport.checkViewportSize();
    }
  }

  private getTotalHeight(): number {
    return this.heights.reduce((acc, curr) => acc + curr, 0);
  }

  private getOffsetByMsgIdx(idx: number): number {
    return this.heights.slice(0, idx).reduce((acc, curr) => acc + curr, 0);
  }

  private determineItemsCountInViewport(startIdx: number): number {
    if (!this.viewport) {
      return 0;
    }

    let totalSize = 0;
    const viewportSize = this.viewport.getViewportSize();

    for (let i = startIdx; i < this.heights.length; i++) {
      const height = this.heights[i];
      totalSize += height;

      if (totalSize >= viewportSize) {
        return i - startIdx + 1;
      }
    }

    return 0;
  }

  private _getItemIdxByOffset(offset: number): number {
    let accumOffset = 0;

    for (let i = 0; i < this.heights.length; i++) {
      const height = this.heights[i];
      accumOffset += height;

      if (accumOffset >= offset) {
        return i;
      }
    }

    return 0;
  }

  private updateRenderedRange() {
    if (!this.viewport) {
      return;
    }

    const scrollOffset = this.viewport.measureScrollOffset();
    const scrollIdx = this._getItemIdxByOffset(scrollOffset);
    const dataLength = this.viewport.getDataLength();
    const renderedRange = this.viewport.getRenderedRange();
    const range = {
      start: renderedRange.start,
      end: renderedRange.end,
    };

    range.start = Math.max(0, scrollIdx - this.topOffset);
    range.end = Math.min(
      dataLength,
      scrollIdx + this.determineItemsCountInViewport(scrollIdx) + this.bottomOffset,
    );

    this.viewport.setRenderedRange(range);
    this.viewport.setRenderedContentOffset(this.getOffsetByMsgIdx(range.start));
    this.scrolledIndexChange$.next(scrollIdx);
  }

  onDataLengthChanged(): void {
    if (!this.viewport) {
      return;
    }

    this.viewport.setTotalContentSize(this.getTotalHeight());
    this.updateRenderedRange();
  }

  onContentScrolled(): void {
    if (this.viewport) {
      this.updateRenderedRange();
    }
  }

  scrollToIndex(index: number, behavior: ScrollBehavior): void {
    if (!this.viewport) {
      return;
    }

    const offset = this.getOffsetByMsgIdx(index);
    this.viewport.scrollToOffset(offset, behavior);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onContentRendered(): void {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onRenderedOffsetChanged(): void {}
}
